<template>
  <div class="container-fluid mt-2">
    <div v-if="currentConsumption">
      <div class="input-group mb-3">
        <RouterLink :to="TranslationsService.i18nRoute({ name: 'consumptions' })" class="btn btn-outline-secondary" active-class="">
          <font-awesome-icon :icon="['fas', 'chevron-left']" />
          {{ $t('global.list') }}
        </RouterLink>
        <button v-if="currentConsumption.status == CONSUMPTION_HEAD_STATUS.pending" type="button" @click="acceptConsumption" class="btn btn-primary" :disabled="isSaving">
          <font-awesome-icon :icon="['fas', 'check']" />
          {{ $t('action.consumeListed') }}
        </button>
        <button v-if="!currentConsumption.nonBillable && currentConsumption.exportStatus == CONSUMPTION_HEAD_EXPORT_STATUS.failed" type="button" @click="retryConsumptionExport" class="btn btn-outline-primary">
          <font-awesome-icon :icon="['fas', 'upload']" />
          {{ $t('page.consumption.retryExport') }}
        </button>
        <button v-if="!currentConsumption.nonBillable && currentConsumption.exportStatus == CONSUMPTION_HEAD_EXPORT_STATUS.failed" type="button" @click="cancelConsumptionExport" class="btn btn-outline-primary">
          <font-awesome-icon :icon="['fas', 'xmarks-lines']" />
          {{ $t('page.consumption.cancelExport') }}
        </button>
        <button v-if="(userSettingsStore.data.developerMode || currentConsumption.exportStatus == CONSUMPTION_HEAD_EXPORT_STATUS.failed) && extensionActive" type="button" @click="saveToExternal" class="btn btn-outline-success">
          <font-awesome-icon :icon="['fas', 'copy']" />
          {{ $t('action.copyToExtension') }}
        </button>
        <button v-else-if="userSettingsStore.data.developerMode || currentConsumption.exportStatus == CONSUMPTION_HEAD_EXPORT_STATUS.failed" type="button" @click="saveToClipboard" class="btn btn-outline-primary">
          <font-awesome-icon :icon="['fas', 'copy']" />
          {{ $t('action.copyToClipboard') }}
        </button>
      </div>

      <div class="d-flex border-bottom gap-3 align-items-center">
        <div class="d-none d-lg-block">
          {{ $t('field.generic.id') }}<br>
          {{ $t('field.masterdata.patientCode') }}
        </div>
        <div>
          <span class="fw-bold text-primary">{{ currentConsumption.id.toString().padStart(6, '0') }}</span><br>
          <span class="fw-bold text-secondary">{{ currentConsumption.referenceCode }}</span>
        </div>
        <div v-if="currentConsumption.nonBillable">
          <font-awesome-layers fixed-width>
            <font-awesome-icon :icon="['fas', 'slash']" :mask="['fas', 'coins']" class="text-muted" />
            <font-awesome-icon :icon="['fas', 'slash']" transform="up-1 right-1" class="text-danger" />
          </font-awesome-layers>
          {{ $t('field.consumption.nonBillable') }}
        </div>
        <div v-else>
          <font-awesome-icon :icon="['fas', 'clipboard-medical']" class="me-2" />
          <ConsumptionStatus :status="currentConsumption.status" :title="currentConsumption.exportReference"/>
          <br>
          <font-awesome-icon :icon="['fas', 'upload']" class="me-2" />
          <ConsumptionExportStatus :status="currentConsumption.exportStatus" :title="currentConsumption.exportResult"/>
        </div>
        <div>
          {{ $t('field.generic.created') }}<br>
          <AuditItem :at="currentConsumption.audit.createdAt" :by="currentConsumption.audit.createdBy" />
        </div>
        <div v-if="currentConsumption.audit.createdAt !== currentConsumption.audit.updatedAt">
          {{ $t('field.generic.updated') }}<br>
          <AuditItem :at="currentConsumption.audit.updatedAt" :by="currentConsumption.audit.updatedBy" />
        </div>
      </div>

      <div class="d-flex border-bottom gap-3 align-items-center" v-if="userSettingsStore.data.developerMode">
        <div class="fw-bold text-primary">{{ currentConsumption.exportReference }}</div>
        <div class="fw-bold text-secondary">{{ currentConsumption.exportResult }}</div>
      </div>

      <ConsumptionLineList :consumption-id="currentConsumption.id"/>
    </div>
    <div v-else>
      <span >{{ $t('error.noData') }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useConsumptionsStore, useForeignItemsStore, useItemsStore, useSupplierItemsStore, useUserSettingsStore } from '@/stores';
import { ToastOptions, toast } from 'vue3-toastify';
import TranslationsService from '@/services/translations';
import ConsumptionLineList from "@/components/consumptions/ConsumptionLineList.vue";
import { IConsumption, IConsumptionLine } from "@/models/responses/IConsumption";
import { onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import AuditItem from "@/components/AuditItem.vue";
import ConsumptionStatus from '@/components/consumptions/ConsumptionStatus.vue';
import ConsumptionExportStatus from '@/components/consumptions/ConsumptionExportStatus.vue';
import { checkExtensionActive, storeToExtension } from '@/services';
import { CONSUMPTION_HEAD_EXPORT_STATUS, CONSUMPTION_HEAD_STATUS } from '@/models/Enums';
import SupplierSignatureItem from '@/models/frontend/SupplierSignatureItem';

const props = defineProps({
  consumptionId: { type: Number, required: true }
});

const i18n = useI18n();
const userSettingsStore = useUserSettingsStore();
const itemStore = useItemsStore();
const consumptionsStore = useConsumptionsStore();
const foreignItemsStore = useForeignItemsStore();
const supplierItemsStore = useSupplierItemsStore();

const retryExportToastId = 'retryExport';

const { error: consumptionStoreError } = consumptionsStore;

const currentConsumption = ref<IConsumption | undefined>(consumptionsStore.getById(props.consumptionId));

const isSaving = ref(false);
const consumedItems = ref<SupplierSignatureItem[]>([]);
const extensionActive = ref<boolean>(false);

onMounted(async () => {
  if (currentConsumption?.value == undefined) {
    await consumptionsStore.fetch(props.consumptionId);
    currentConsumption.value = consumptionsStore.getById(props.consumptionId);
  }

  consumedItems.value = currentConsumption.value.lines.map((item: IConsumptionLine) => {
    const lineItem = itemStore.getById(item.itemId);
    return new SupplierSignatureItem(
      lineItem,
      lineItem.baseUnitCode,
      item.quantity,
      item.lot,
      item.bestBefore,
      item.expiry,
      false,
      item.trackStock,
      supplierItemsStore.getByItemId(item.itemId),
      foreignItemsStore.getByItemId(item.itemId)
    );
  });

  extensionActive.value = await checkExtensionActive();
});

const acceptConsumption = async () => {
  if (isSaving.value) return;

  if (currentConsumption?.value == undefined) {
    toast.error(i18n.t('page.consumption.notFoundError'));
    return;
  }

  isSaving.value = true;

  await consumptionsStore.accept(currentConsumption.value.id);
  if (consumptionStoreError.value) {
    toast.error(i18n.t('page.consumption.acceptError', { error: consumptionStoreError.value }), { theme: 'colored' });
    isSaving.value = false;
  }
  else {
    currentConsumption.value.status = CONSUMPTION_HEAD_STATUS.inProgress;
    toast.info(i18n.t('page.consumption.accepted', { referenceCode: currentConsumption.value.referenceCode }), { theme: 'colored', autoClose: 1500 } as ToastOptions);

    isSaving.value = false;
  }
};

const saveToExternal = async () => {
  if (!await saveToExtension()) {
    await saveToClipboard();
  }
}

const saveToExtension = async () => {
  if (!currentConsumption.value) return false;

  return await storeToExtension('consumption', {
    patientCode: currentConsumption.value.referenceCode,
    consumedItems: getAllConsumedItems()
  });
}

const saveToClipboard = async () => {
  if (!currentConsumption.value) return;

  // const consumptionData = {
  //   patientCode: currentConsumption.value.referenceCode,
  //   consumedItems: getAllConsumedItems()
  // };
  // const consumptionDataString = JSON.stringify(consumptionData, null, 2);
  const consumptionDataString = jsonToCsv(getSlimConsumedItems());
  navigator.clipboard.writeText(consumptionDataString).then(
    () => toast.success(i18n.t('statement.contentCopied'), { autoClose: 1000 }),
    () => toast.error(i18n.t('error.copiedError'), { autoClose: 1000 })
  );
};

const retryConsumptionExport = async () => {
  if (!currentConsumption.value) return;

  isSaving.value = true;

  toast.loading(i18n.t('page.consumption.retryingExport'), {
    toastId: retryExportToastId,
    autoClose: false,
    theme: toast.THEME.COLORED,

    transition: toast.TRANSITIONS.ZOOM,
    position: toast.POSITION.TOP_RIGHT,
  } as ToastOptions);

  await consumptionsStore.retryExport(currentConsumption.value.id, onExportApiFulfilled, onExportApiRejected);
};

const onExportApiFulfilled = async (consumption: IConsumption) => {
  isSaving.value = false;
  if (consumption.exportStatus === CONSUMPTION_HEAD_EXPORT_STATUS.successful) {
    toast.update(retryExportToastId, {
      render: i18n.t('page.consumption.exportSuccess', { referenceCode: consumption.referenceCode }),
      type: toast.TYPE.SUCCESS,
      isLoading: false,
      autoClose: 10000,
      closeOnClick: true
    } as ToastOptions);
  }
  else {
    toast.update(retryExportToastId, {
      render: i18n.t('page.consumption.exportRetryError', { error: consumption.exportResult }),
      type: toast.TYPE.ERROR,
      isLoading: false,
      closeOnClick: true
    } as ToastOptions);
  }
  currentConsumption.value = consumptionsStore.getById(props.consumptionId);
};

const onExportApiRejected = (error: Error) => {
  isSaving.value = false;
  toast.update(retryExportToastId, {
    render: i18n.t('page.consumption.exportRetryError', { error: error.message }),
    closeOnClick: true,
    type: toast.TYPE.ERROR,
    isLoading: false
  } as ToastOptions);
  currentConsumption.value = consumptionsStore.getById(props.consumptionId);
};

const cancelConsumptionExport = async () => {
  if (!currentConsumption.value) return;

  isSaving.value = true;

  await consumptionsStore.cancelExport(currentConsumption.value.id);
  if (consumptionStoreError.value) {
    toast.error(i18n.t('page.consumption.exportCancelError', { error: consumptionStoreError.value }), { theme: 'colored' });
    isSaving.value = false;
  }
  else {
    currentConsumption.value = consumptionsStore.getById(props.consumptionId);
    toast.info(i18n.t('page.consumption.exportCancelled', { referenceCode: currentConsumption.value.referenceCode }), { theme: 'colored', autoClose: 1500 } as ToastOptions);

    isSaving.value = false;
  }
};

function getAllConsumedItems() {
  let consumedItemsList: {
    itemCode: string;
    itemName: string;
    itemDescription: string;
    quantity: number;
    unitCode: string;
    lot: string | undefined;
    bestBefore: string | undefined;
    expiry: string | undefined;
    trackStock: boolean;
    foreignItemCode: string | undefined;
    supplierItemCode: string | undefined;
  }[] = [];
  consumedItemsList = consumedItems.value.map((item) => {
    return {
      itemCode: item.item.code,
      itemName: item.item.name,
      itemDescription: item.item.description,
      quantity: item.quantity,
      unitCode: item.item.baseUnitCode,
      lot: item.lot,
      bestBefore: item.bestBefore,
      expiry: item.expiry,
      trackStock: item.foreignItem?.trackStock ?? true,
      foreignItemCode: item.foreignItem?.code,
      supplierItemCode: item.supplierItem?.code
    };
  });
  return consumedItemsList;
}

function getSlimConsumedItems() {
  let consumedItemsList: {
    itemCode: string;
    quantity: number;
    unitCode: string;
    itemName: string;
    itemDescription: string;
  }[] = [];
  consumedItemsList = consumedItems.value.map((item) => {
    return {
      itemCode: item.foreignItem?.code ?? item.item.code,
      quantity: item.quantity,
      unitCode: item.item.baseUnitCode,
      itemName: item.item.name,
      itemDescription: item.item.description,
    };
  });
  return consumedItemsList;
}

function jsonToCsv(jsonArray: any[]): string {
    if (!Array.isArray(jsonArray) || jsonArray.length === 0) {
        return '';
    }

    // Extract headers (keys) from the first object
    const headers = Object.keys(jsonArray[0]);
    const csvRows = [];

    // Add headers as the first row
    csvRows.push(headers.join(','));

    // Add data rows
    jsonArray.forEach(obj => {
        const row = headers.map(header => {
            const value = obj[header];
            // Escape double quotes and wrap values in quotes if necessary
            return typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value;
        });
        csvRows.push(row.join(','));
    });

    // Combine rows with newline characters
    return csvRows.join('\n');
}
</script>
